import { useState, useRef, useCallback, useEffect } from 'react';

// ── Types ──
export interface GameTx {
  id: string;
  txid: string;       // real txid
  address: string;     // sender address (chars used for visual)
  x: number;
  y: number;
  speed: number;
  size: 'small' | 'medium' | 'large' | 'xl';
  vsize: number;
  feeRate: number;
  valueSats: number;
  score: number;
  width: number;       // visual width in px
  chars: string[];     // address chars for rendering
  captured: boolean;
  fadeOut: number;      // 0 = visible, >0 = fading
}

export type GameRank = 'Pleb' | 'Stacker' | 'Strong Hand' | 'Block Hunter' | 'Flow Whale' | "Miner's Favorite";

export interface MiningResultData {
  nonce: number;
  hash: string;
  attempts: number;
  elapsedSec: number;
  luck: number;
  difficulty: string;
  manualMined: boolean;
}

export interface GameResult {
  score: number;
  capturedPct: number;
  rank: GameRank;
  blockHeight: number;
  txCaptured: number;
  txTotal: number;
  satsCaptured: number;
  blockBonus: boolean;
  blockOverflow: boolean;
  congestionTier: 'calm' | 'busy' | 'hot';
  scoreMultiplier: number;
  avgFeeRate: number;
  usedBlockSpace: number;
  blockCapacity: number;
  optimalScore: number;
  miningResult?: MiningResultData;
}

export type GamePhase = 'idle' | 'intro' | 'playing' | 'ending' | 'mining' | 'result';

// ── Real tx from mempool.space ──
interface RawMempoolTx {
  txid: string;
  fee: number;
  vsize: number;
  value: number;
}

// ── Congestion tiers ──

type CongestionTier = 'calm' | 'busy' | 'hot';

interface TierProfile {
  spawnIntervalMs: number;    // base ms between spawns
  speedBase: number;          // base fall speed
  speedFeeMax: number;        // max additional speed from fee rate
  timeRampFactor: number;     // how much speed ramps over the round (0-1 = +0-100%)
  scoreMultiplier: number;    // score scaling factor
  sizeWeights: Record<GameTx['size'], number>; // probability weights for size override
}

const TIER_PROFILES: Record<CongestionTier, TierProfile> = {
  calm: {
    spawnIntervalMs: 180,
    speedBase: 2.2,
    speedFeeMax: 3.0,
    timeRampFactor: 0.4,
    scoreMultiplier: 1.0,
    sizeWeights: { small: 25, medium: 30, large: 28, xl: 17 },
  },
  busy: {
    spawnIntervalMs: 120,
    speedBase: 2.6,
    speedFeeMax: 4.0,
    timeRampFactor: 0.6,
    scoreMultiplier: 1.3,
    sizeWeights: { small: 15, medium: 28, large: 35, xl: 22 },
  },
  hot: {
    spawnIntervalMs: 80,
    speedBase: 3.0,
    speedFeeMax: 5.5,
    timeRampFactor: 0.8,
    scoreMultiplier: 1.7,
    sizeWeights: { small: 8, medium: 20, large: 40, xl: 32 },
  },
};

function getCongestionTier(congestion: number): CongestionTier {
  if (congestion < 30) return 'calm';
  if (congestion < 65) return 'busy';
  return 'hot';
}

/** Weighted random pick from size weights */
function weightedSizePick(weights: Record<GameTx['size'], number>): GameTx['size'] {
  const entries: [GameTx['size'], number][] = [
    ['small', weights.small], ['medium', weights.medium],
    ['large', weights.large], ['xl', weights.xl],
  ];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.random() * total;
  for (const [size, w] of entries) {
    r -= w;
    if (r <= 0) return size;
  }
  return 'small';
}

// ── Helpers ──

function classifySize(vsize: number): GameTx['size'] {
  if (vsize < 250) return 'small';
  if (vsize < 800) return 'medium';
  if (vsize < 2500) return 'large';
  return 'xl';
}

/** Score = total fee in sats (feeRate × vsize), with tier multiplier */
function scoreForTx(feeRate: number, vsize: number, multiplier = 1): number {
  const totalFee = feeRate * vsize; // sats
  return Math.round(totalFee * multiplier);
}

function widthForSize(s: GameTx['size']): number {
  switch (s) {
    case 'small': return 3;
    case 'medium': return 5;
    case 'large': return 8;
    case 'xl': return 12;
  }
}

/** Rank based on avg fee rate efficiency of captured txs */
function getRank(avgFeeRate: number): GameRank {
  if (avgFeeRate >= 25) return "Miner's Favorite";
  if (avgFeeRate >= 18) return 'Flow Whale';
  if (avgFeeRate >= 12) return 'Block Hunter';
  if (avgFeeRate >= 7) return 'Strong Hand';
  if (avgFeeRate >= 3) return 'Stacker';
  return 'Pleb';
}

async function fetchRecentTxs(): Promise<RawMempoolTx[]> {
  try {
    const res = await fetch('https://mempool.space/api/mempool/recent');
    if (!res.ok) return [];
    const txs: RawMempoolTx[] = await res.json();
    return txs.slice(0, 10);
  } catch {
    return [];
  }
}

async function fetchTxAddress(txid: string): Promise<string> {
  try {
    const res = await fetch(`https://mempool.space/api/tx/${txid}`);
    if (!res.ok) return txid;
    const data = await res.json();
    return data.vin?.[0]?.prevout?.scriptpubkey_address || txid;
  } catch {
    return txid;
  }
}

const CAPTURE_RADIUS = 34;
const BLOCK_FOUND_BANNER_MS = 3000;
const BLOCK_CAPACITY_VB = 100_000; // target block space in vBytes

export function useStackSatsGame(
  congestion: number,
  blockJustFound: boolean,
  blockHeight: number,
) {
  const [phase, setPhase] = useState<GamePhase>('idle');
  const [score, setScore] = useState(0);
  const [capturedWeight, setCapturedWeight] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [txCaptured, setTxCaptured] = useState(0);
  const [txTotal, setTxTotal] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<GameResult | null>(null);
  const [captures, setCaptures] = useState<{ x: number; y: number; score: number; id: string }[]>([]);
  const [blockFoundBanner, setBlockFoundBanner] = useState(false);
  const [usedBlockSpace, setUsedBlockSpace] = useState(0);

  const txsRef = useRef<GameTx[]>([]);
  const buddyPosRef = useRef({ x: 0, y: 0 });
  const gameActiveRef = useRef(false);
  const startTimeRef = useRef(0);
  const spawnTimerRef = useRef(0);
  const blockHeightAtStartRef = useRef(0);
  const scoreRef = useRef(0);
  const capturedWeightRef = useRef(0);
  const totalWeightRef = useRef(0);
  const txCapturedRef = useRef(0);
  const txTotalRef = useRef(0);
  const satsCapturedRef = useRef(0);
  const usedBlockSpaceRef = useRef(0);
  // Track all spawned tx scores for optimal calculation
  const allSpawnedTxsRef = useRef<{ score: number; vsize: number; feeRate: number }[]>([]);

  // Pool of real txs to spawn from, refreshed periodically
  const txPoolRef = useRef<{ tx: RawMempoolTx; address: string }[]>([]);
  const txPoolIndexRef = useRef(0);
  const fetchTimerRef = useRef(0);

  // Fetch real txs with addresses
  const refreshTxPool = useCallback(async () => {
    const txs = await fetchRecentTxs();
    if (txs.length === 0) return;
    // Fetch addresses in parallel (first 10)
    const addresses = await Promise.allSettled(
      txs.map(tx => fetchTxAddress(tx.txid))
    );
    const pool: { tx: RawMempoolTx; address: string }[] = [];
    for (let i = 0; i < txs.length; i++) {
      const addr = addresses[i].status === 'fulfilled' ? (addresses[i] as PromiseFulfilledResult<string>).value : txs[i].txid;
      pool.push({ tx: txs[i], address: addr });
    }
    txPoolRef.current = pool;
    txPoolIndexRef.current = 0;
  }, []);

  const startGame = useCallback(() => {
    setPhase('intro');
    // Pre-fetch tx pool during intro
    refreshTxPool();
  }, [refreshTxPool]);

  const launchRound = useCallback(() => {
    txsRef.current = [];
    scoreRef.current = 0;
    capturedWeightRef.current = 0;
    totalWeightRef.current = 0;
    txCapturedRef.current = 0;
    txTotalRef.current = 0;
    satsCapturedRef.current = 0;
    usedBlockSpaceRef.current = 0;
    allSpawnedTxsRef.current = [];
    setScore(0);
    setCapturedWeight(0);
    setTotalWeight(0);
    setTxCaptured(0);
    setTxTotal(0);
    setElapsed(0);
    setResult(null);
    setCaptures([]);
    setUsedBlockSpace(0);
    startTimeRef.current = Date.now();
    fetchTimerRef.current = Date.now();
    blockHeightAtStartRef.current = blockHeight;
    gameActiveRef.current = true;
    setPhase('playing');
  }, [blockHeight]);

  // Capture all uncaptured on-screen txs (used on block found bonus)
  const captureAllOnScreen = useCallback(() => {
    const bonus: { x: number; y: number; score: number; id: string }[] = [];
    for (const tx of txsRef.current) {
      if (!tx.captured) {
        tx.captured = true;
        scoreRef.current += tx.score;
        capturedWeightRef.current += tx.score;
        txCapturedRef.current += 1;
        satsCapturedRef.current += tx.valueSats;
        bonus.push({ x: tx.x, y: tx.y, score: tx.score, id: tx.id });
      }
    }
    if (bonus.length > 0) {
      setScore(scoreRef.current);
      setCapturedWeight(capturedWeightRef.current);
      setTxCaptured(txCapturedRef.current);
      setCaptures(prev => [...prev, ...bonus].slice(-12));
    }
  }, []);

  const endGame = useCallback((blockBonus = false, blockOverflow = false) => {
    if (!gameActiveRef.current) return;

    // Block found bonus: capture everything on screen first
    if (blockBonus) {
      captureAllOnScreen();
    }

    gameActiveRef.current = false;
    setPhase('ending');

    // Block fill percentage
    const blockFillPct = Math.min(100, (usedBlockSpaceRef.current / BLOCK_CAPACITY_VB) * 100);

    // Avg fee rate of captured txs
    const avgFeeRate = usedBlockSpaceRef.current > 0
      ? scoreRef.current / usedBlockSpaceRef.current
      : 0;

    // Compute optimal score: greedy knapsack — pick txs with highest fee rate first
    const sorted = [...allSpawnedTxsRef.current].sort((a, b) => b.feeRate - a.feeRate);
    let optSpace = 0;
    let optScore = 0;
    for (const t of sorted) {
      if (optSpace + t.vsize <= BLOCK_CAPACITY_VB) {
        optSpace += t.vsize;
        optScore += t.score;
      }
    }

    const tier = getCongestionTier(congestion);
    const multiplier = TIER_PROFILES[tier].scoreMultiplier;

    const res: GameResult = {
      score: scoreRef.current,
      capturedPct: Math.round(blockFillPct * 10) / 10,
      rank: getRank(avgFeeRate / multiplier),
      blockHeight: blockHeightAtStartRef.current,
      txCaptured: txCapturedRef.current,
      txTotal: txTotalRef.current,
      satsCaptured: satsCapturedRef.current,
      blockBonus,
      blockOverflow,
      congestionTier: tier,
      scoreMultiplier: multiplier,
      avgFeeRate: Math.round(avgFeeRate * 10) / 10,
      usedBlockSpace: usedBlockSpaceRef.current,
      blockCapacity: BLOCK_CAPACITY_VB,
      optimalScore: optScore,
    };
    setResult(res);

    // Transition to mining phase instead of result
    setTimeout(() => setPhase('mining'), 600);
  }, [captureAllOnScreen, congestion]);

  // Lock block early — skip to mining with current template
  const lockBlock = useCallback(() => {
    if (!gameActiveRef.current) return;
    endGame(false);
  }, [endGame]);

  const completeMining = useCallback((miningData?: MiningResultData) => {
    setResult(prev => prev ? { ...prev, miningResult: miningData } : prev);
    setPhase('result');
  }, []);

  // End on block found during game — with bonus capture
  useEffect(() => {
    if (phase === 'playing' && blockJustFound && blockHeight > blockHeightAtStartRef.current) {
      // Show in-game block found banner for a few seconds before ending
      setBlockFoundBanner(true);
      const timer = setTimeout(() => {
        setBlockFoundBanner(false);
        endGame(true);
      }, BLOCK_FOUND_BANNER_MS);
      return () => clearTimeout(timer);
    }
  }, [blockJustFound, blockHeight, phase, endGame]);

  const dismiss = useCallback(() => {
    setPhase('idle');
    setResult(null);
    gameActiveRef.current = false;
  }, []);

  // Game tick — called from the game component's rAF
  const tick = useCallback((playAreaW: number, playAreaH: number) => {
    if (!gameActiveRef.current) return txsRef.current;

    const now = Date.now();
    const elapsedSec = (now - startTimeRef.current) / 1000;
    setElapsed(elapsedSec);

    // No time cap — game ends when block is full or real block found

    // Refresh tx pool every 8 seconds
    if (now - fetchTimerRef.current > 8000) {
      fetchTimerRef.current = now;
      refreshTxPool();
    }

    // ── Tier-driven spawn logic ──
    const tier = getCongestionTier(congestion);
    const profile = TIER_PROFILES[tier];
    const spawnInterval = Math.max(120, profile.spawnIntervalMs);

    if (now - spawnTimerRef.current > spawnInterval) {
      spawnTimerRef.current = now;

      const pool = txPoolRef.current;
      if (pool.length > 0) {
        const entry = pool[txPoolIndexRef.current % pool.length];
        txPoolIndexRef.current++;

        const rawTx = entry.tx;
        const address = entry.address;
        const feeRate = rawTx.vsize > 0 ? rawTx.fee / rawTx.vsize : 1;

        // Size: blend real vsize classification with tier-weighted random
        const realSize = classifySize(rawTx.vsize);
        const size = Math.random() < 0.4 ? weightedSizePick(profile.sizeWeights) : realSize;

        // Speed: tier base + fee influence + time ramp
        const feeNorm = Math.min(1, Math.max(0, (feeRate - 1) / 30));
        const timeRamp = 1 + (elapsedSec / 120) * profile.timeRampFactor;
        const speed = (profile.speedBase + feeNorm * profile.speedFeeMax) * timeRamp;

        // Score: fee-based with tier multiplier
        const txScore = scoreForTx(feeRate, rawTx.vsize, profile.scoreMultiplier);

        const tailLen = Math.max(4, Math.min(20, Math.floor(rawTx.vsize / 150)));
        const chars: string[] = [];
        for (let i = 0; i < tailLen; i++) {
          chars.push(address[i % address.length]);
        }

        const tx: GameTx = {
          id: `${now}-${rawTx.txid.slice(0, 6)}`,
          txid: rawTx.txid,
          address,
          x: 10 + Math.random() * (playAreaW - 20),
          y: -20,
          speed,
          size,
          vsize: rawTx.vsize,
          feeRate: Math.round(feeRate * 10) / 10,
          valueSats: rawTx.value,
          score: txScore,
          width: widthForSize(size),
          chars,
          captured: false,
          fadeOut: 0,
        };

        txsRef.current.push(tx);
        allSpawnedTxsRef.current.push({ score: tx.score, vsize: tx.vsize, feeRate: tx.feeRate });
        totalWeightRef.current += tx.score;
        txTotalRef.current += 1;
        setTotalWeight(totalWeightRef.current);
        setTxTotal(txTotalRef.current);
      }
    }

    // Move txs & check collision
    const bx = buddyPosRef.current.x;
    const by = buddyPosRef.current.y;
    const newCaptures: typeof captures = [];

    for (const tx of txsRef.current) {
      if (tx.captured) {
        tx.fadeOut += 1;
        continue;
      }
      tx.y += tx.speed;

      // Collision check — only capture if block has space
      const dx = Math.abs(tx.x - bx);
      const dy = Math.abs(tx.y - by);
      if (dx < CAPTURE_RADIUS && dy < CAPTURE_RADIUS) {
        // Overflow check: if this tx would exceed capacity, reject it and end game
        if (usedBlockSpaceRef.current + tx.vsize > BLOCK_CAPACITY_VB) {
          endGame(false, true);
          break;
        }

        tx.captured = true;
        scoreRef.current += tx.score;
        capturedWeightRef.current += tx.score;
        txCapturedRef.current += 1;
        satsCapturedRef.current += tx.valueSats;
        usedBlockSpaceRef.current += tx.vsize;
        setScore(scoreRef.current);
        setCapturedWeight(capturedWeightRef.current);
        setTxCaptured(txCapturedRef.current);
        setUsedBlockSpace(usedBlockSpaceRef.current);
        newCaptures.push({ x: tx.x, y: tx.y, score: tx.score, id: tx.id });

        // Auto-end if block is exactly full
        if (usedBlockSpaceRef.current >= BLOCK_CAPACITY_VB) {
          endGame(false);
          break;
        }
      }
    }

    if (newCaptures.length > 0) {
      setCaptures(prev => [...prev, ...newCaptures].slice(-8));
    }

    // Remove off-screen or fully faded
    txsRef.current = txsRef.current.filter(
      tx => tx.y < playAreaH + 30 && tx.fadeOut < 20,
    );

    return txsRef.current;
  }, [congestion, endGame, refreshTxPool]);

  const updateBuddyPos = useCallback((x: number, y: number) => {
    buddyPosRef.current = { x, y };
  }, []);

  const capturedPct = totalWeight > 0 ? (capturedWeight / totalWeight) * 100 : 0;

  return {
    phase,
    score,
    capturedPct: Math.round(capturedPct * 10) / 10,
    elapsed,
    result,
    captures,
    blockFoundBanner,
    congestionTier: getCongestionTier(congestion),
    usedBlockSpace,
    blockCapacity: BLOCK_CAPACITY_VB,
    txs: txsRef,
    startGame,
    launchRound,
    endGame,
    lockBlock,
    dismiss,
    completeMining,
    tick,
    updateBuddyPos,
    maxRoundSec: Infinity,
  };
}
